import Phaser from "phaser";
import { ItemUI } from "./item-ui";
import { playSound } from "../utils/sound-utils";
import { Item, SlotType } from "../models/common";

/**
 * Represents a circular action slot rendered in Phaser that can hold an item.
 */
export class ActionSlot {
  private scene: Phaser.Scene;
  public container: Phaser.GameObjects.Container;
  public circle: Phaser.GameObjects.Graphics;
  public iconOrText: Phaser.GameObjects.Image | Phaser.GameObjects.Text | null =
    null;
  public item: Item | null;
  private readonly radius: number = 24;
  private readonly itemIconSize: number = 40;
  private isBlinking: boolean = false;
  private blinkTween: Phaser.Tweens.Tween | null = null;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    item: Item | null = null,
  ) {
    this.scene = scene;
    this.item = item;
    this.circle = scene.add.graphics();
    this.drawCircle();
    this.container = scene.add.container(x, y, [this.circle]);
    this.container.setDepth(100002);
    this.iconOrText = null;
    if (item) this.setItem(item);

    // Add pointerdown event for click handling
    this.container.setSize(this.radius * 2, this.radius * 2);

    // Ensure each slot's container is interactive and above the container
    this.container.setScrollFactor(0);
    this.container.setDepth(100000);
    this.container.setInteractive({ useHandCursor: true });
    this.container.on("pointerdown", () => {
      this.handleClick();
    });
  }
  /**
   * Call this when the slot is clicked (automatically wired in constructor).
   */
  handleClick() {
    if (!this.item) return;
    // Play pick-item sound
    playSound(this.scene, "pick-item-sound");
    if (this.item.definition.type === "Equipment") {
      window.socket.emit("equip-item", {
        itemId: this.item.id,
        slotType: SlotType.ACTION,
      });
    } else if (
      this.item.definition.type === "Consumable" ||
      this.item.definition.type === "Ammo"
    ) {
      const itemName = this.item.definition.name?.toLowerCase() || "";
      if (itemName === "bandage") {
        playSound(this.scene, "bandage", null, 0.5);
      } else if (itemName === "health pack" || itemName === "health pill") {
        playSound(this.scene, "heal", null, 0.5);
      }
      window.socket.emit("use-item", {
        itemId: this.item.id,
        slotType: SlotType.ACTION,
      });
    } else if (this.item.definition.type === "Throwable") {
      // Activate throwable mode - emit to scene
      this.scene.events.emit("throwable-activated", this.item);
      // Reinitialize throwable item after activation
      if (this.item && this.item.definition.type === "Throwable") {
        (this.scene as any).activeThrowableItem = this.item;
      }
      // Only start blinking if not in base scene
      const isBaseScene =
        this.scene.scene && this.scene.scene.key === "BaseScene";
      if (!isBaseScene) {
        this.startBlinking();
      }
    }
  }

  startBlinking() {
    if (this.isBlinking || !this.circle) return;
    this.isBlinking = true;

    // Create blinking animation with yellow color
    this.blinkTween = this.scene.tweens.add({
      targets: this.circle,
      alpha: { from: 1, to: 0.3 },
      duration: 300,
      ease: "Power2",
      yoyo: true,
      repeat: -1,
    });

    // Override circle color to yellow while blinking
    this.drawCircle(0xffff00);
  }

  stopBlinking() {
    if (!this.isBlinking) return;
    this.isBlinking = false;

    if (this.blinkTween) {
      this.blinkTween.stop();
      this.blinkTween = null;
    }

    if (this.circle) {
      this.circle.alpha = 1;
      this.drawCircle(); // Reset to normal color
    }
  }

  setItem(item: Item | null) {
    this.item = item;
    this.renderItem();
  }

  private drawCircle(strokeColor: number = 0xffffff) {
    this.circle.clear();
    this.circle.fillStyle(0x282828, 0.85);
    this.circle.fillCircle(0, 0, this.radius);
    this.circle.lineStyle(3, strokeColor, 1);
    this.circle.strokeCircle(0, 0, this.radius);
  }

  private renderItem() {
    // Clear any existing item sprite
    if (this.iconOrText) {
      this.container.remove(this.iconOrText);
      this.iconOrText.destroy();
      this.iconOrText = null;
    }

    // Additional safety: remove all children except the circle
    // This ensures no stray sprites remain in the container
    const children = [...this.container.list];
    for (const child of children) {
      if (child !== this.circle) {
        this.container.remove(child);
        if (child && typeof child.destroy === "function") {
          child.destroy();
        }
      }
    }

    if (this.item) {
      // Use ItemUI to render only the sprite (not name or price)
      const sprite = ItemUI.render(
        this.scene,
        this.item.definition,
        -this.itemIconSize / 2,
        -this.itemIconSize / 2,
        this.itemIconSize,
      );

      if (sprite && this.container) {
        this.container.add(sprite);
        this.iconOrText = sprite;
      }

      // Add ammo indicator for weapons (positioned in lower-right of circle slot)
      const ammoIndicator = ItemUI.renderAmmoIndicator(
        this.scene,
        this.item,
        -this.radius,
        -this.radius,
        this.radius * 2,
      );
      if (ammoIndicator && this.container) {
        this.container.add(ammoIndicator);
      }

      const countBadge = ItemUI.renderCountBadge(
        this.scene,
        this.item,
        -this.radius,
        -this.radius,
        this.radius * 2,
      );
      if (countBadge && this.container) {
        this.container.add(countBadge);
      }
    }
  }

  destroy() {
    this.stopBlinking();
    if (this.container) {
      this.container.destroy();
    }
  }
}
